import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import type { QuestionOption } from "../questionConfig";

interface MultiSelectProps {
  label: string;
  description?: string;
  options: QuestionOption[];
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  required?: boolean;
  maxSelections?: number;
}

export function MultiSelect({
  label,
  description,
  options,
  value = [],
  onChange,
  maxSelections,
}: MultiSelectProps) {
  const handleToggle = (optionValue: string) => {
    const currentValue = value || [];
    const isSelected = currentValue.includes(optionValue);

    if (isSelected) {
      onChange(currentValue.filter((v) => v !== optionValue));
    } else {
      if (maxSelections && currentValue.length >= maxSelections) {
        // Remove first item and add new one
        onChange([...currentValue.slice(1), optionValue]);
      } else {
        onChange([...currentValue, optionValue]);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-[var(--dug-text-primary)]">{label}</h3>
        {description && (
          <p className="text-sm text-[var(--dug-text-muted)] mt-1">{description}</p>
        )}
        {maxSelections && (
          <p className="text-xs text-[var(--dug-text-muted)] mt-1">
            Select up to {maxSelections}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = (value || []).includes(option.value);
          return (
            <motion.button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`
                relative px-4 py-2 rounded-full border-2 transition-all flex items-center gap-2
                ${isSelected
                  ? "border-[var(--dug-coral)] bg-[var(--dug-coral)] text-[var(--dug-text-inverse)]"
                  : "border-[var(--dug-border)] hover:border-[var(--dug-coral)]/50 bg-[var(--dug-bg-surface)] text-[var(--dug-text-primary)]"
                }
              `}
            >
              {isSelected && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                >
                  <Check className="w-4 h-4" />
                </motion.span>
              )}
              <span className="font-medium text-sm">{option.label}</span>
            </motion.button>
          );
        })}
      </div>

      {value && value.length > 0 && (
        <p className="text-sm text-[var(--dug-text-muted)]">
          {value.length} selected
        </p>
      )}
    </div>
  );
}
